import { SearchRegistry } from '../types';

export const insightsAnalyticsSearchEntries: SearchRegistry = {
  '/insights-and-analytics/transaction-exchange-log': {
    keywords: [
      'exchange logs',
      'transaction exchange log',
      'data exchange',
      'exchange log',
      'integration logs',
      'bosch exchange',
      'transaction log',
      'message log',
      'data transfer status',
      'failed transfers',
      'successful transfers',
      'severity filter',
      'station id at bosch',
      'bosch station id',
      'no personal data',
      'maximum range 3 months',
      'data transfers',
      'log search',
    ],
    questions: [
      'Where do I see the transaction exchange logs?',
      'How do I check the data-exchange events for Bosch?',
      'How do I filter exchange logs by status (ERROR vs INFO)?',
      'How do I search exchange logs by Bosch station ID?',
      'What is the maximum time range I can query?',
    ],
    faqs: [
      {
        q: 'How do I see only the failed data transfers in the exchange log?',
        a: 'On Insights & Analytics → Data Exchange Monitoring → Exchange Logs, set the Data Transfer Status filter to "Failed Transfers Only" (severity ERROR). The result is scoped to errored exchanges in the date range; switch to "Successful Transfers Only" (severity INFO) for the inverse view.',
      },
      {
        q: 'What is the maximum date range I can query in the exchange log?',
        a: 'The exchange-log search caps at 3 months. If you need older logs, run the search in successive 3-month windows; the page shows a warning if you pick a wider range.',
      },
    ],
  },
  '/insights-and-analytics/transaction-exchange-log-by-order': {
    keywords: [
      'exchange log by order',
      'transaction exchange log',
      'order log',
      'order exchange',
      'lookup by order',
      'order trace',
      'message trace',
      'exchange log for order',
      'order id log',
      'per-order log',
    ],
    questions: [
      'How do I see the exchange log for a specific order?',
      'Where do I trace data exchange events by order id?',
      'How do I drill into the per-order data-exchange messages?',
    ],
  },
  '/insights-and-analytics/overview': {
    keywords: [
      'insights overview',
      'analytics overview',
      'reports overview',
      'kpi overview',
      'dashboard',
      'insights and analytics',
      'kpis',
      'metrics',
      'insights & analytics overview',
      'analytics landing',
    ],
    questions: ['Where is the insights & analytics overview?', 'How do I see the high-level KPIs?'],
  },
  '/insights-and-analytics/monitoring/partner-alerts': {
    keywords: [
      'partner alerts',
      'receipt export failures',
      'partner monitoring alerts',
      'alert list',
      'alert table',
      'review status',
      'partner alert monitoring',
      'alert filters',
      'searchKey',
      'orderUuid alert search',
      'mobiko alerts',
      'staging alert data',
      'production alert data',
    ],
    questions: [
      'Where do I review partner alerts?',
      'How do I find receipt-export failures for my partner account?',
      'How do I filter partner alerts by order UUID or review status?',
      'How do I share a deep link to the current partner-alert search?',
    ],
    urlParameters: [
      'env',
      'startDate',
      'endDate',
      'searchKey',
      'orderUuid',
      'reviewStatus',
      'page',
    ],
    faqs: [
      {
        q: 'How do I filter and share the current partner-alert search?',
        a: 'Use the Partner Alerts filters for date range, free-text search key, order UUID and review status, then click Share. The generated deeplink preserves the selected environment plus `startDate`, `endDate`, `searchKey`, `orderUuid`, `reviewStatus` and `page`.',
      },
    ],
  },
  '/insights-and-analytics/monitoring/partner-alerts/detail': {
    keywords: [
      'partner alert detail',
      'alert detail',
      'event id lookup',
      'order uuid alert lookup',
      'alert comments',
      'alert review status',
      'order snapshot',
      'error details',
      'alert deeplink',
      'partner monitoring detail',
    ],
    questions: [
      'How do I open a partner alert by event ID?',
      'How do I look up the latest partner alert for an order UUID?',
      'Where do I review comments, status and error details for one partner alert?',
    ],
    urlParameters: ['eventId', 'orderUuid', 'environment'],
    faqs: [
      {
        q: 'How do I deep link to one partner alert?',
        a: 'Open Partner Alerts Detail with `eventId` for a direct lookup, or with `orderUuid` to resolve the latest matching alert. Add `environment=staging` for staging data; without it, the page defaults to production.',
      },
    ],
  },
  '/insights-and-analytics/monitoring/connect-tracking': {
    keywords: [
      'ryd connect',
      'connect tracking',
      'connect journey',
      'fleet api tracking',
      'frontend backend correlation',
      'webview analytics',
      'user journey',
      'partner journey',
      'session timeline',
      'order link',
      'staging connect data',
      'production connect data',
      'environment selector',
    ],
    questions: [
      'How do I trace a ryd Connect user journey?',
      'Where do I see my Connect frontend activity next to the fleet API calls?',
      'How do I follow one order across the webview and the backend?',
      'How do I switch the Connect tracking view between staging and production?',
    ],
    urlParameters: [
      'environment',
      'sponsorUserGroup',
      'userId',
      'orderId',
      'dateFrom',
      'dateTo',
      'page',
    ],
    faqs: [
      {
        q: 'How do I follow a single ryd Connect journey end to end?',
        a: 'Open Insights & Analytics → Monitoring → ryd Connect Tracking. The page auto-scopes to your partner; set the date range (and optionally a sub-fleet, user id or order id) and Search. The timeline interleaves your Connect webview events with the matching fleet API calls, with a badge showing how each event was linked (order, session+time or partner scope).',
      },
      {
        q: 'How do I switch the Connect tracking view between staging and production?',
        a: 'Use the environment selector in the filters. It scopes both sides: backend audit data comes from the matching environment dataset, and frontend webview events are filtered by host (connect.rydapi-test.com for staging, connect.rydapi.com for production).',
      },
    ],
  },
  '/dashboard/api-requests-analytics': {
    keywords: [
      'api requests',
      'api monitoring',
      'request analytics',
      'rate limit',
      'request volume',
      'latency',
      'p95',
      'p99',
      'api usage',
      'request stats',
      'api gateway analytics',
      'ryd api gateway',
      'partner environment switcher',
      'staging vs production',
      'api requests summary',
      'failure analysis',
      'http status codes',
      'request summary',
      'environment switch',
    ],
    questions: [
      'How many API requests am I sending?',
      'Where do I see request volume and latency for my API integration?',
      'How do I check whether I am hitting rate limits?',
      'Why is my failure rate higher in production than in staging?',
      'How do I switch between staging and production analytics?',
    ],
    faqs: [
      {
        q: 'Where do I see my recent API request volume and latency?',
        a: 'Open Insights & Analytics → Monitoring → API Requests Monitoring. The Requests Summary tab shows volume and latency for the selected time window; the Failure Analysis tab breaks down errors by status code and endpoint. Use the Partner Environment Switcher in the header to flip between staging and production.',
      },
      {
        q: 'How do I compare staging and production API behaviour?',
        a: 'The header includes a Partner Environment Switcher; toggling it persists your choice (in `api-requests-analytics-environment`) and reloads both the Requests Summary and Failure Analysis tabs against the chosen environment.',
      },
    ],
  },
  '/dashboard/order-insights-analytics': {
    keywords: [
      'orders monitoring',
      'order insights',
      'order analytics',
      'order metrics',
      'order volume',
      'success rate',
      'failure rate',
      'order kpi',
      'overview & guide',
      'order volume analysis',
      'order creation trends',
      'order failure insights',
      'state & error analysis',
      'environment selector',
      'production live',
      'staging test',
      'order failure',
      'order trends',
    ],
    questions: [
      'How are my orders performing?',
      'What is my order success rate this week?',
      'Where do I see order volume and failure rate?',
      'How do I see order creation trends over time?',
      'How do I analyse the most common failure states?',
    ],
    faqs: [
      {
        q: 'Which views are available on Orders Monitoring?',
        a: 'Order Insights Analytics has five tabs: Overview & Guide, Order Volume Analysis, Order Creation Trends, Order Failure Insights and State & Error Analysis. Switch via the pill nav at the top; an Environment Selector lets you flip between Production (live) and Staging (test).',
      },
    ],
  },
  '/insights-and-analytics/webhook-monitoring': {
    keywords: [
      'webhook monitoring',
      'webhook delivery',
      'webhook errors',
      'webhook retries',
      'failed webhooks',
      'webhook health',
      'webhook stats',
      'delivery latency',
      'redelivery',
      'webhook status code',
      'webhook 4xx',
      'webhook 5xx',
    ],
    questions: [
      'How do I check whether my webhook endpoint is receiving events?',
      'Where do I see failed webhook deliveries?',
      'How many webhook retries did we attempt yesterday?',
      'What latency do my webhook endpoints have?',
    ],
    faqs: [
      {
        q: 'Where do I see whether my webhook endpoint is healthy?',
        a: 'Open Insights & Analytics → Monitoring → Webhook Monitoring. The page lists recent deliveries, latency, and retries for your registered webhook URLs and flags endpoints with elevated errors.',
      },
    ],
  },
  '/dashboard/order-analytics-gas': {
    keywords: [
      'order statistics gas',
      'fuelling statistics',
      'gas statistics',
      'order analytics gas',
      'fuel volume',
      'litres',
      'turnover',
      'gross sales',
      'fuelling kpis',
      'gross volume',
      'station turnover',
      'fuel turnover',
    ],
    questions: [
      'Where do I see fuelling order statistics?',
      'How do I check my fuel order volume per station?',
      'How much turnover did fuelling generate last week?',
    ],
  },
  // Restored under its new path once the export moved to rp-integrations. It was previously
  // '/dashboard/active-stations', reading from partner-performance-guardian (shared-key-only
  // auth, key no longer shipped).
  '/insights-and-analytics/station-management/petrol-stations': {
    keywords: [
      'petrol stations',
      'fuel stations',
      'active stations',
      'rydpay stations',
      'station export',
      'download stations',
      'download fuel stations',
      'petrol station download',
      'station list',
      'csv export',
      'excel export',
      'nuts code',
      'nuts region',
    ],
    questions: [
      'How do I download the list of active petrol stations?',
      'Where do I export all rydpay stations as Excel or CSV?',
      'How do I get the POI IDs of the petrol stations?',
      'Which NUTS region does a station belong to?',
    ],
  },
  '/insights-and-analytics/station-management/brands': {
    keywords: [
      'station brands',
      'brand list',
      'fuel brands',
      'station brand mapping',
      'brand directory',
      'shell aral esso',
      'brand normalisation',
      'brand catalog',
      'brand metadata',
      'brand identity',
    ],
    questions: [
      'Where do I see the supported station brands?',
      'How are station brands mapped in ryd?',
      'How does ryd normalise brand names across providers?',
    ],
  },
  '/insights-and-analytics/station-management/car-wash-stations': {
    keywords: [
      'car wash',
      'car wash stations',
      'car wash list',
      'wash stations',
      'autowäsche',
      'wash availability',
      'wash directory',
      'wash inventory',
      'car-wash availability',
    ],
    questions: [
      'Which stations support car wash?',
      'Where do I find the list of car-wash stations?',
      'How do I check car-wash availability at a specific station?',
    ],
  },
  '/insights-and-analytics/station-management/poi-statistics': {
    keywords: [
      'poi statistics',
      'poi ingestion',
      'station data refresh',
      'plugsurfing sync',
      'openstreetmap import',
      'osm import',
      'ev charging data',
      'petrol station data',
      'car wash data',
      'publish gate',
      'ingestion run',
      'provider budget',
    ],
    questions: [
      'How do I refresh the POI statistics data?',
      'When was the station data last published?',
      'Why was an ingestion run rejected?',
      'How many provider calls has today used?',
      'Which BigQuery tables hold the station data?',
      'Are the daily call budgets set by ryd or by the provider?',
    ],
    faqs: [
      {
        q: 'How do I start a POI statistics refresh?',
        a: 'Not from the portal — the page is read-only by design, because a refresh crawls a rate-limited partner API and can run for minutes. Refreshes are started with the ingestion runner script in the rp-integrations repository, under docs/2026-08-poi-statistics/scripts/, by someone with repository access.',
      },
      {
        q: 'An ingestion run shows as Rejected — what went wrong?',
        a: 'Probably nothing. Rejected means the publish gate refused a run whose row count dropped further below the published snapshot than the tolerance allows, and the previous snapshot is still serving. That gate exists because the 2024 pipeline silently lost 13,615 stations and published the result. If the smaller snapshot is genuinely correct, an operations_admin can force-publish it with a written reason.',
      },
      {
        q: 'Are the call budgets shown on the page limits set by PlugSurfing and OpenStreetMap?',
        a: 'No. They are ryd-set guards enforced by our own pacer. PlugSurfing has never published its real rate limits, so the 1-second pacing is a conservative value that has worked rather than a contract term, and the daily cap exists to stop a runaway loop. The one provider-imposed limit we know of is Overpass allowing 2 concurrent requests per IP.',
      },
      {
        q: 'Which BigQuery tables hold the POI data, and what is in them?',
        a: 'Dataset poi_statistics, EU multi-region, with two raw tables: raw_plugsurfing_locations (one EV charging location per run, with EVSEs, connectors and tariffs nested in a JSON payload) and raw_osm_pois (one OpenStreetMap element per run, with every tag, split into petrol and car wash by the category column). Both store provider payloads unmodified; nothing downstream of raw exists yet.',
      },
      {
        q: 'Why do my POI queries return double the expected station count?',
        a: 'Raw tables keep every run ever, and duplicate rows within a run are legitimate (a re-written page or a delta overlap). Filter to the newest run_id for the scope and count with COUNT(DISTINCT ...). For OpenStreetMap, identity is the pair (osm_type, osm_id), not osm_id alone.',
      },
    ],
  },
  '/insights-and-analytics/product-metadata/concepts': {
    keywords: [
      'product metadata concepts',
      'metadata concepts',
      'product taxonomy',
      'metadata model',
      'concepts',
      'product definitions',
      'product attributes glossary',
      'normalised product names',
      'sku concepts',
      'metadata vocabulary',
    ],
    questions: [
      'What is product metadata in ryd?',
      'Where do I read about the metadata concepts?',
      'How is a product attribute defined in ryd?',
    ],
  },
  '/insights-and-analytics/product-metadata': {
    keywords: [
      'product metadata',
      'metadata',
      'product catalog',
      'product attributes',
      'product configuration',
      'product list',
      'normalised product names',
      'unified product metadata',
      'product directory',
      'sku metadata',
    ],
    questions: [
      'Where do I see the product metadata?',
      'How do I look up a product attribute or normalised name?',
      'How do I find which SKU maps to a normalised product?',
    ],
  },
  '/partner/fleet-operations/sub-fleets': {
    keywords: [
      'sub-fleets',
      'sub fleets',
      'fleet operations',
      'partner fleet operations',
      'fleet api sub fleets',
      'fleet api sponsors',
      'fleet sponsor list',
      'sub-fleet list',
      'sub-fleet details',
      'fleet details',
      'fleet drivers',
      'driver list',
      'license plates',
      'fleet contract details',
      'fleet bank details',
      'fleet manager',
      'signatory',
      'staging sub-fleets',
      'production sub-fleets',
      'environment selector',
    ],
    questions: [
      'Where do I see my API partner sub-fleets?',
      'How do I open sub-fleet details?',
      'Where can I check drivers and license plates for a sub-fleet?',
      'How do I switch between staging and production sub-fleets?',
    ],
    faqs: [
      {
        q: 'Where do I see sub-fleet details for a fleet API partner?',
        a: 'Open Insights & Analytics -> Fleet Operations -> Sub-fleets. Select a sub-fleet to see its company, contract, address, bank, contact and driver information for the selected environment.',
      },
    ],
  },
  '/partner/fleet-operations/transactions': {
    keywords: [
      'transactions',
      'transaction list',
      'fleet transactions',
      'partner transactions',
      'fleet operations transactions',
      'fleet api transactions',
      'orders',
      'order list',
      'order state',
      'completed transactions',
      'creation date range',
      'date range',
      'sub-fleet filter',
      'license plate filter',
      'license plates',
      'partner extra data',
      'transaction details',
      'station',
      'total amount',
      'csv export',
      'export to csv',
      'async export',
      'partner export centre',
      'staging transactions',
      'production transactions',
      'environment selector',
    ],
    questions: [
      'Where do I search fleet API transactions?',
      'How do I filter transactions by sub-fleet or license plate?',
      'How do I export transactions to CSV?',
      'Where do I see partner extra data for a transaction?',
      'How do I switch between staging and production transactions?',
    ],
    faqs: [
      {
        q: 'How do I search transactions across sub-fleets?',
        a: 'Open Insights & Analytics -> Fleet Operations -> Transactions. Select the environment, date range, order state, sub-fleets and optional license plates, then click Search.',
      },
      {
        q: 'How do I export fleet transactions to CSV?',
        a: 'Use Export to CSV on the Transactions page after setting the filters. The export is queued asynchronously and appears in Exports -> Export Centre when the CSV file is ready.',
      },
    ],
  },
  '/partner/fleet-operations/orders/order-detail': {
    keywords: [
      'order details',
      'fleet order details',
      'fleet operation order detail',
      'partner fleet order detail',
      'fleet api order detail',
      'order uuid lookup',
      'order uuid search',
      'single order lookup',
      'deeplink order detail',
      'order deeplink',
      'order summary',
      'order owner',
      'owning sub-fleet',
      'sub-fleet owner',
      'invoice status',
      'invoice records',
      'sale order status',
      'troubleshooting order',
      'order log viewer',
      'staging order details',
      'production order details',
      'environment selector',
      'share order link',
    ],
    questions: [
      'Where do I look up a fleet order by UUID?',
      'How do I open a direct link to a fleet order?',
      'How do I check which sub-fleet owns an order?',
      'Where do I see invoice status for one fleet order?',
      'How do I open a fleet order in troubleshooting?',
      'How do I switch a fleet order lookup between staging and production?',
    ],
    urlParameters: ['orderUuid', 'environment'],
    faqs: [
      {
        q: 'How do I open a fleet order detail page directly?',
        a: 'Open Insights & Analytics -> Fleet Operations -> Order details, or use /partner/fleet-operations/orders/order-detail?orderUuid=<uuid>. Add &environment=staging for staging; without an environment parameter the page defaults to production.',
      },
      {
        q: 'What can I inspect on the fleet order detail page?',
        a: 'The page shows the order summary, owning sub-fleet or company, invoice status records, and a troubleshooting link for deeper request, response and webhook diagnostics.',
      },
      {
        q: 'How do I share a fleet order lookup?',
        a: 'Search for the order UUID, verify the selected environment, then use Share link. The copied deeplink includes the order UUID and includes environment=staging only for staging lookups.',
      },
    ],
  },
  '/insights-and-analytics/order-search-by-location': {
    keywords: [
      'order search',
      'search by location',
      'orders by station',
      'orders by poi',
      'station orders',
      'orders near me',
      'location search',
      'poi search',
      'station id search',
      'orders by poi wrapper',
    ],
    questions: [
      'How do I find orders for a specific petrol station?',
      'Where do I search orders by location or POI?',
      'How do I see orders that happened at a particular POI ID?',
    ],
    faqs: [
      {
        q: 'How do I find orders that happened at a specific station?',
        a: 'Open Insights & Analytics → Order Management → Order Search by Location. Pick the station or POI and a time window — the search lists matching orders and links to their detail page.',
      },
    ],
  },
  '/insights-and-analytics/orders/allguth-failed-orders': {
    keywords: [
      'fehlgeschlagene transaktionen',
      'allguth failed orders',
      'failed transactions',
      'failed orders',
      'allguth',
      'error orders',
      'transaktionsfehler',
      'allguth fehler',
      'allguth error',
      'allguth investigation',
    ],
    questions: [
      'Where do I see Allguth failed transactions?',
      'How do I investigate Allguth failed orders?',
      'Welche Allguth-Transaktionen sind fehlgeschlagen?',
    ],
  },
  '/insights-and-analytics/ip/order-search': {
    keywords: [
      'orders',
      'order search',
      'individual partner orders',
      'partner orders',
      'order list',
      'find order',
      'order search bosch',
      'individual partner order search',
    ],
    questions: [
      'Where do I see my orders as an individual partner?',
      'How do I search my orders?',
      'Where is the Bosch order search?',
    ],
  },
  '/insights-and-analytics/api/order-list': {
    keywords: [
      'order list',
      'api orders',
      'api partner orders',
      'all orders',
      'order overview',
      'order table',
      'environment selector',
      'production live',
      'staging test',
      'search criteria',
      'order uuid search',
      'order state',
      'state selection',
      'csv download',
      'csv export limit 5000',
      '90 days max',
      'inclusive date',
      'pagination',
      'time zone',
    ],
    questions: [
      'Where do I see all of my API partner orders?',
      'How do I filter the API order list by status?',
      'How do I export my API orders?',
      'Why is the CSV export capped at 5,000 orders?',
      'Why does the search refuse a date range over 90 days?',
    ],
    faqs: [
      {
        q: 'Why is the CSV export capped at 5,000 orders?',
        a: 'The API Partner Order List streams a CSV from a paginated query and caps the export at 5,000 rows to keep the request bounded. To export more, narrow the time range or order state and run successive exports.',
      },
      {
        q: 'Why does the search reject a date range longer than 90 days?',
        a: 'The Search Date Range field enforces a 90-day maximum to keep the result set tractable. The page validates this client-side and displays a warning when the chosen window is wider; split the search into 90-day windows.',
      },
      {
        q: 'How do I switch between staging and production order lists?',
        a: 'Use the Environment Selector at the top of the API Order List. Your choice is persisted in `order-search-api-partner-environment` so the page remembers it next time.',
      },
    ],
  },
};
